import { clearAiEstimates } from "./ai/component-draft";
import { getDefinition } from "./catalog";
import { configuredChargeCurrent } from "./charge-current";
import { isAcTerminalId } from "./ac";
import type { ComponentDefinition, System, SystemNode, Terminal } from "./types";

export type ComponentCustomizationPlan = {
  nodes: SystemNode[];
  /** Stored automatic fuse ratings need refreshing after a current edit. */
  electricalChanged: boolean;
};

/**
 * Plan an inline definition edit without mutating the node or its catalog part.
 * Undefined inputs are ignored; only an explicit `unset` removes a field.
 * The caller enforces equipment limits, applies the nodes, and refreshes fuses.
 */
export function planComponentCustomization(
  system: System,
  nodeId: string,
  partial: Partial<ComponentDefinition>,
  opts?: { unset?: readonly (keyof ComponentDefinition)[] },
): ComponentCustomizationPlan {
  const applied = Object.fromEntries(
    Object.entries(partial).filter(([, v]) => v !== undefined),
  ) as Partial<ComponentDefinition>;
  const unset = opts?.unset ?? [];
  const nodes = system.nodes.map((n) => {
    if (n.id !== nodeId || n.kind !== "component") return n;
    const baseDef =
      n.customDefinition ?? getDefinition(n.definitionId, n.customDefinition);
    if (!baseDef) return n;
    const merged: ComponentDefinition = { ...baseDef, ...applied };
    for (const key of unset) delete merged[key];
    const nextDef: ComponentDefinition = clearAiEstimates(merged, [
      ...Object.keys(applied),
      ...unset,
    ]);
    // Terminal maxAmp tracks ampOutput/ampDraw — a stale cap clamps the cable
    // current. Only raise, never shrink, or a manual upsize is lost.
    // An unverified placeholder has no declared connector ampacity. Editing its
    // operating current does not publish that missing hardware rating.
    if (!nextDef.requestedModel && (partial.ampOutput != null || partial.ampDraw != null)) {
      const hasInput = nextDef.terminals.some((t) => t.direction === "input");
      const hasOutput = nextDef.terminals.some(
        (t) => t.direction === "output",
      );
      const multiPort = hasInput && hasOutput;
      // Multi-port parts carry their own current per rail, so bump only that side.
      const tracksOutput = (t: Terminal): boolean =>
        multiPort
          ? t.direction === "output"
          : t.direction === "output" || t.direction === "bidi";
      const tracksDraw = (t: Terminal): boolean =>
        multiPort
          ? t.direction === "input"
          : t.direction === "input" || t.direction === "bidi";
      nextDef.terminals = nextDef.terminals.map((t) => {
        // ampDraw/ampOutput describe DC rails. A charger's 125 A DC output
        // must never overwrite its 50/60 A AC contacts or signal-port rating.
        if (isAcTerminalId(t.id) || t.polarity === "signal") return t;
        let max = t.maxAmp;
        if (
          partial.ampOutput != null &&
          tracksOutput(t) &&
          max < partial.ampOutput
        ) {
          max = partial.ampOutput;
        }
        if (
          partial.ampDraw != null &&
          tracksDraw(t) &&
          max < partial.ampDraw
        ) {
          max = partial.ampDraw;
        }
        return max === t.maxAmp ? t : { ...t, maxAmp: max };
      });
    }
    return {
      ...n,
      definitionId: "custom",
      customDefinition: nextDef,
      ...(n.chargeCurrentLimitA === undefined ? {} : { chargeCurrentLimitA: configuredChargeCurrent(n, nextDef) }),
    };
  });
  return {
    nodes,
    electricalChanged:
      partial.ampDraw != null ||
      partial.ampOutput != null ||
      partial.outputCurrentLimitsA != null ||
      unset.includes("outputCurrentLimitsA") ||
      partial.ampSurge != null,
  };
}
